import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  getIntermediarias,
  criarIntermediaria,
  atualizarIntermediaria,
  excluirIntermediaria,
  toggleStatusIntermediaria,
  reordenarIntermediarias,
} from '~/Services/Service';

const validationSchema = Yup.object().shape({
  tipo: Yup.string().required('Tipo é obrigatório'),
  descricao: Yup.string().required('Descrição é obrigatória'),
  meses: Yup.number().min(0, '>= 0'),
  percentual: Yup.number().min(0, '>= 0'),
  adimplencia_meses: Yup.number().min(0, '>= 0'),
  adimplencia_percentual: Yup.number().min(0, '>= 0'),
});

const filtroOpcoes = [
  { value: 'A', label: 'Ativas' },
  { value: 'I', label: 'Inativas' },
  { value: 'T', label: 'Todas' },
];

const ModalIntermediaria = ({ show, onHide, familia, onCountChange }) => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('A');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemExcluir, setItemExcluir] = useState(null);

  const carregarDados = useCallback(async () => {
    if (!familia) return;
    setLoading(true);
    try {
      const res = await getIntermediarias(familia);
      const todos = res || [];
      setDados(todos);
      const ativos = todos.filter((i) => i.ativo);
      if (onCountChange) onCountChange(ativos.length);
    } catch {
      toast.error('Erro ao carregar intermediárias');
    } finally {
      setLoading(false);
    }
  }, [familia, onCountChange]);

  useEffect(() => {
    if (show) carregarDados();
  }, [show, carregarDados]);

  const dadosFiltrados = dados.filter((d) => {
    if (filtro === 'A') return d.ativo;
    if (filtro === 'I') return !d.ativo;
    return true;
  });

  const handleNovo = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEditar = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleExcluir = (item) => {
    setItemExcluir(item);
    setShowConfirm(true);
  };

  const confirmarExclusao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado) return;
    try {
      await excluirIntermediaria(itemExcluir.codigo);
      toast.success('Intermediária excluída com sucesso');
      carregarDados();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao excluir intermediária';
      toast.error(msg);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      await toggleStatusIntermediaria(item.codigo);
      toast.success('Status alterado');
      carregarDados();
    } catch {
      toast.error('Erro ao alterar status');
    }
  };

  const handleMover = async (item, direcao) => {
    const sorted = [...dados].sort((a, b) => a.ordem - b.ordem || a.codigo - b.codigo);
    const idx = sorted.findIndex((d) => d.codigo === item.codigo);
    const novoIdx = idx + direcao;
    if (novoIdx < 0 || novoIdx >= sorted.length) return;

    const temp = sorted[idx].ordem;
    sorted[idx].ordem = sorted[novoIdx].ordem;
    sorted[novoIdx].ordem = temp;

    if (sorted[idx].ordem === sorted[novoIdx].ordem) {
      sorted[idx].ordem = idx;
      sorted[novoIdx].ordem = novoIdx;
    }

    try {
      await reordenarIntermediarias({
        itens: sorted.map((d, i) => ({ codigo: d.codigo, ordem: i })),
      });
      carregarDados();
    } catch {
      toast.error('Erro ao reordenar');
    }
  };

  const handleSubmitForm = async (values) => {
    setSaving(true);
    try {
      const payload = {
        codigo_familia: familia,
        tipo: values.tipo,
        descricao: values.descricao,
        meses: values.meses,
        percentual: values.percentual,
        adimplencia_meses: values.adimplencia_meses,
        adimplencia_percentual: values.adimplencia_percentual,
      };

      if (editItem) {
        await atualizarIntermediaria(editItem.codigo, payload);
        toast.success('Intermediária atualizada');
      } else {
        await criarIntermediaria(payload);
        toast.success('Intermediária criada');
      }
      setShowForm(false);
      carregarDados();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao salvar intermediária';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const getFormInitialValues = () => {
    if (editItem) {
      return {
        tipo: editItem.tipo || 'C',
        descricao: editItem.descricao || '',
        meses: editItem.meses || 0,
        percentual: editItem.percentual || 0,
        adimplencia_meses: editItem.adimplencia_meses || 0,
        adimplencia_percentual: editItem.adimplencia_percentual || 0,
      };
    }
    return {
      tipo: 'C',
      descricao: '',
      meses: 0,
      percentual: 0,
      adimplencia_meses: 0,
      adimplencia_percentual: 0,
    };
  };

  const sortedFiltrados = [...dadosFiltrados].sort(
    (a, b) => a.ordem - b.ordem || a.codigo - b.codigo
  );

  const renderCard = (item, idx) => (
    <div
      key={item.codigo}
      style={{
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        padding: '10px 14px',
        marginBottom: '8px',
        backgroundColor: item.ativo ? '#fff' : '#f8f9fa',
        opacity: item.ativo ? 1 : 0.7,
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div style={{ fontWeight: 600, fontSize: '14px' }}>
          {item.descricao}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
          <Badge variant={item.tipo === 'C' ? 'primary' : 'warning'}>
            {item.tipo === 'C' ? 'Comercial' : 'Residencial'}
          </Badge>
          <Badge variant={item.ativo ? 'success' : 'secondary'}>
            {item.ativo ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <div style={{ fontSize: '13px', color: '#555' }}>
          <span className="mr-3">
            <strong>Constr:</strong> {item.meses}m / {item.percentual != null ? `${item.percentual}%` : '-'}
          </span>
          <span className="mr-3">
            <strong>Em dia:</strong> {item.adimplencia_meses}m / {item.adimplencia_percentual != null ? `${item.adimplencia_percentual}%` : '-'}
          </span>
          <span>
            <strong>Ordem:</strong> {item.ordem}
          </span>
        </div>

        <div className="d-flex" style={{ gap: '4px' }}>
          <Button
            variant="outline-primary"
            size="sm"
            title="Subir"
            onClick={() => handleMover(item, -1)}
            disabled={idx === 0}
            style={{ padding: '2px 6px' }}
          >
            <FaArrowUp />
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            title="Descer"
            onClick={() => handleMover(item, 1)}
            disabled={idx === sortedFiltrados.length - 1}
            style={{ padding: '2px 6px' }}
          >
            <FaArrowDown />
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            title={item.ativo ? 'Inativar' : 'Ativar'}
            onClick={() => handleToggleStatus(item)}
            style={{ padding: '2px 6px' }}
          >
            <FaToggleOn />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            title="Editar"
            onClick={() => handleEditar(item)}
            style={{ padding: '2px 6px' }}
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            title="Excluir"
            onClick={() => handleExcluir(item)}
            style={{ padding: '2px 6px' }}
          >
            <FaTrash />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Intermediárias</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Select
                value={filtroOpcoes.find((o) => o.value === filtro)}
                onChange={(opt) => setFiltro((opt && opt.value) || 'A')}
                options={filtroOpcoes}
                styles={customStyles}
                components={{ DropdownIndicator }}
              />
            </Col>
            <Col className="d-flex justify-content-end">
              <Button variant="primary" size="sm" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Nova Intermediária
              </Button>
            </Col>
          </Row>

          {loading ? (
            <Loading />
          ) : sortedFiltrados.length === 0 ? (
            <p className="text-center text-muted py-3">
              Nenhuma intermediária encontrada.
            </p>
          ) : (
            sortedFiltrados.map((item, idx) => renderCard(item, idx))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de formulário (stacked) */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editItem ? 'Editar Intermediária' : 'Nova Intermediária'}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={getFormInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmitForm}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit: formSubmit,
          }) => (
            <Form onSubmit={formSubmit}>
              <Modal.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Tipo *</Form.Label>
                      <Form.Control
                        as="select"
                        size="sm"
                        name="tipo"
                        value={values.tipo}
                        onChange={handleChange}
                      >
                        <option value="C">Comercial</option>
                        <option value="R">Residencial</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Descrição *</Form.Label>
                      <Form.Control
                        size="sm"
                        name="descricao"
                        value={values.descricao}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.descricao && errors.descricao}
                        maxLength={50}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.descricao}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Construção meses</Form.Label>
                      <Form.Control
                        size="sm"
                        name="meses"
                        type="number"
                        value={values.meses}
                        onChange={handleChange}
                        min={0}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Construção %</Form.Label>
                      <Form.Control
                        size="sm"
                        name="percentual"
                        type="number"
                        step="0.01"
                        value={values.percentual}
                        onChange={handleChange}
                        min={0}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Em dia meses</Form.Label>
                      <Form.Control
                        size="sm"
                        name="adimplencia_meses"
                        type="number"
                        value={values.adimplencia_meses}
                        onChange={handleChange}
                        min={0}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Em dia %</Form.Label>
                      <Form.Control
                        size="sm"
                        name="adimplencia_percentual"
                        type="number"
                        step="0.01"
                        value={values.adimplencia_percentual}
                        onChange={handleChange}
                        min={0}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <ModalConfirmacao
        show={showConfirm}
        onClose={confirmarExclusao}
        title="Excluir Intermediária"
        message="Deseja realmente excluir esta intermediária?"
      />
    </>
  );
};

export default ModalIntermediaria;
